import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, UserRound, Package, Printer } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { api } from "@/lib/api";
import { Page } from "@/components/Page";

export default function Verify({ user }) {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/products").then(r => { setProducts(r.data); setSelected(r.data[0] || null); }).catch(() => {});
    api.get("/profile").then(r => setProfile(r.data)).catch(() => {});
  }, []);

  const origin = window.location.origin;
  const profileUrl = user?.id ? `${origin}/seller/${user.id}` : "";
  const productUrl = selected ? `${origin}/verify/${selected.id}` : "";
  const firmName = profile?.firm_name || user?.name || "seller";

  const downloadCanvas = (testId, filename) => {
    const canvas = document.querySelector(`[data-testid='${testId}']`);
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL();
    link.click();
    toast.success("QR downloaded");
  };

  const slug = (s) => (s || "seller").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const downloadPoster = () => {
    if (!user?.id) return;
    const canvas = document.querySelector("[data-testid='seller-profile-qr-code']");
    if (!canvas) { toast.error("Profile QR not ready yet"); return; }
    const qrData = canvas.toDataURL("image/png");
    const giItem = profile?.items?.[0]?.name || profile?.gi_registration || "GI authorised product";
    const place = profile?.address || profile?.state || "";

    // A4 portrait 210×297mm
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const green = [11, 107, 63];
    const saffron = [255, 122, 26];
    const ink = [15, 30, 20];

    // Green top band
    doc.setFillColor(...green);
    doc.rect(0, 0, 210, 42, "F");
    doc.setFillColor(...saffron);
    doc.rect(0, 42, 210, 4, "F");

    doc.setTextColor(255, 216, 161);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("TRUE VISION WELFARE SOCIETY", 105, 18, { align: "center" });

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text("Verified GI Authorised User", 105, 30, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(220, 232, 224);
    doc.text("Scan the QR to verify this profile", 105, 38, { align: "center" });

    // Firm name
    doc.setTextColor(...ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text(firmName, 105, 66, { align: "center", maxWidth: 180 });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(80, 92, 84);
    doc.text(giItem, 105, 76, { align: "center", maxWidth: 180 });
    if (place) doc.text(place, 105, 84, { align: "center", maxWidth: 180 });

    // QR image centered
    const qrSize = 110;
    doc.addImage(qrData, "PNG", (210 - qrSize) / 2, 96, qrSize, qrSize);

    // Scan-to-verify caption
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...green);
    doc.text("SCAN TO VERIFY", 105, 220, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80, 92, 84);
    doc.text("Opens the full authorised-user profile on truevision", 105, 228, { align: "center" });

    // URL box
    doc.setDrawColor(...green);
    doc.setLineWidth(0.3);
    doc.roundedRect(20, 236, 170, 12, 2, 2);
    doc.setTextColor(...green);
    doc.setFontSize(10);
    doc.text(profileUrl, 105, 243.5, { align: "center", maxWidth: 165 });

    // Bottom saffron footer band
    doc.setFillColor(...saffron);
    doc.rect(0, 275, 210, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Authenticity you can scan.", 105, 285, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Registered under the GI Act, 1999 · IP India · truevision", 105, 292, { align: "center" });

    doc.save(`${slug(firmName)}-stall-poster.pdf`);
    toast.success("Poster downloaded");
  };


  return (
    <Page title="Authenticity QR" subtitle="Two QR codes for one story — your verified profile, and each product's traceable record.">
      {/* Profile QR */}
      <section className="qr-block" data-testid="seller-profile-qr-section">
        <div className="qr-layout">
          <div className="qr-placeholder">
            {user?.id ? (
              <QRCodeCanvas value={profileUrl} size={190} includeMargin data-testid="seller-profile-qr-code" />
            ) : <UserRound size={100} />}
            <p>Scan to open the full seller profile</p>
          </div>
          <div>
            <p className="eyebrow"><UserRound size={12} style={{ verticalAlign: "-2px" }} /> COMPLETE PROFILE</p>
            <h3>My True Vision profile QR</h3>
            <p className="subtitle">
              A single QR that opens your full public profile — firm details, GI item, achievements, gallery and all listed products.
              Print it on business cards, exhibition stalls, and shopfront signage.
            </p>
            {profileUrl && (
              <p className="muted" style={{ wordBreak: "break-all", marginBottom: 12 }} data-testid="seller-profile-qr-url">{profileUrl}</p>
            )}
            <button
              className="primary-btn"
              disabled={!user?.id}
              data-testid="download-profile-qr-button"
              onClick={() => downloadCanvas("seller-profile-qr-code", `${slug(firmName)}-profile-qr.png`)}
            >
              Download profile QR <QrCode size={17} />
            </button>
            <button
              className="secondary-btn"
              disabled={!user?.id}
              data-testid="download-stall-poster-button"
              onClick={downloadPoster}
              style={{ marginLeft: 10 }}
            >
              <Printer size={15} /> Download stall poster (PDF)
            </button>
          </div>
        </div>
      </section>

      {/* Product QR */}
      <section className="qr-block" data-testid="product-qr-section" style={{ marginTop: 28 }}>
        <div className="qr-layout">
          <div className="qr-placeholder">
            {selected ? (
              <QRCodeCanvas value={productUrl} size={170} includeMargin data-testid="product-qr-code" />
            ) : <QrCode size={100} />}
            <p>{selected ? "Scan to verify product" : "Publish a product first"}</p>
          </div>
          <div>
            <p className="eyebrow"><Package size={12} style={{ verticalAlign: "-2px" }} /> PER-PRODUCT TRUST MARKER</p>
            <h3>Product authenticity QR</h3>
            <p className="subtitle">
              {selected
                ? `A downloadable QR for ${selected.name}. Buyers scan it to view the product's authenticity record.`
                : "QR generation becomes available once a product is published."}
            </p>
            {products.length > 1 && (
              <select data-testid="qr-product-select" value={selected?.id || ""} onChange={e => setSelected(products.find(p => p.id === e.target.value))}>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <button
              className="primary-btn"
              disabled={!selected}
              data-testid="generate-qr-button"
              onClick={() => downloadCanvas("product-qr-code", `${slug(selected?.name)}-authenticity-qr.png`)}
            >
              Download product QR <QrCode size={17} />
            </button>
          </div>
        </div>
      </section>
    </Page>
  );
}
